import { useCallback } from 'react';
import { z } from 'zod';
import { errorMessageFromResponse, useApi, type ApiFetchInit } from './api';
import { blankToUndefined, extractUserMessage } from '@shared/error-message';
import { ErrorObjectSchema } from '@shared/schemas/error-envelope';
import { describeRequestInput, logApiValidationFailure } from './apiValidationLog';
import { toStripView } from './stripView';
import { ApiProblemError } from './apiProblemError';
import { safeParseResponse, type JsonParseError } from '@shared/parse-json';

export {
  ApiProblemError,
  NETWORK_FAILURE_MESSAGE,
  UNEXPECTED_RESPONSE_MESSAGE,
  WriteRefusedError,
  type ApiProblemKind,
  type WriteRefusal,
} from './apiProblemError';

/**
 * The `apiFetch` shape returned by `useApi()` — accepted as a param so a
 * plain (non-hook) function can still call it. Moved here from
 * `components/admin/ranker/api.ts` (plan `2026-09-25-003-pr1683-premerge-fixes-plan.md`,
 * U3); that module now re-exports this type so its ~10 fetchers keep
 * compiling unchanged.
 */
export type ApiFetch = ReturnType<typeof useApi>['apiFetch'];

/**
 * The three string fields a `problem()` envelope (`src/http/responder.ts`)
 * may carry, read through a schema rather than a bare cast — the same
 * trust-boundary treatment `SignalActionProblemSchema`
 * (`components/flow/signals/useSignalsMutations.ts`) gave them before this
 * class replaced it.
 */
const ProblemFieldsSchema = z
  .object({
    title: z.string(),
    detail: z.string(),
    code: z.string(),
    // Salesforce re-auth dead end fix: `toSalesforceProblem`
    // (`src/http/sfdc-problem.ts`) emits a 401 with `oauthUrl` (and a 429
    // may carry `retryAfterSec`) spread onto the `problem()` envelope by
    // `src/http/responder.ts`'s catch-all rest spread. Read here the same
    // way the mini-app boundary already does (`mini-apps/_shared/fetch-error.ts`'s
    // `throwFetchError`) so core Terra callers get the same fields.
    oauthUrl: z.string(),
    retryAfterSec: z.number(),
  })
  .partial();

/** True when a body read failed because the request was cancelled mid stream
 *  (headers arrived, the abort signal fired during the read). That must NOT
 *  collapse into "malformed"/"http" — the write may have committed. */
function isAbortedRead(error: JsonParseError): boolean {
  return error.kind === 'read' && error.cause instanceof DOMException && error.cause.name === 'AbortError';
}

/**
 * Non-hook. Wraps one `apiFetch` call end to end:
 * - a rejection becomes `kind: 'network'` (or `'aborted'` for an
 *   `AbortError`/`DOMException`), except an `ApiProblemError` (a
 *   `WriteRefusedError` from `apiFetch`), which is rethrown as is;
 * - a non-2xx response becomes `kind: 'http'`, reading `title`/`detail`/
 *   `code` through a schema and deriving `userMessage` via the cross-app
 *   precedence chain;
 * - a 2xx body that fails `schema` becomes `kind: 'malformed'` — fail-closed,
 *   never returns the unparsed payload;
 * - otherwise resolves with the parsed, schema-shaped data.
 */
export async function fetchJson<S extends z.ZodType>(
  apiFetch: ApiFetch,
  schema: S,
  input: RequestInfo | URL,
  init?: ApiFetchInit
): Promise<z.output<S>> {
  let response: Response;
  try {
    response = await apiFetch(input, init);
  } catch (err) {
    if (err instanceof ApiProblemError) throw err;
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new ApiProblemError({ kind: 'aborted', status: 0, code: 'ABORTED', cause: err });
    }
    throw new ApiProblemError({ kind: 'network', status: 0, code: 'NETWORK_ERROR', cause: err });
  }

  if (!response.ok) {
    // A non-JSON, empty, or consumed body is `body: undefined`, not an error.
    const parsedBody = await safeParseResponse(z.unknown(), response);
    if (!parsedBody.success && isAbortedRead(parsedBody.error)) {
      throw new ApiProblemError({
        kind: 'aborted',
        status: response.status,
        code: 'ABORTED',
        cause: parsedBody.error.cause,
      });
    }
    const body: unknown = parsedBody.success ? parsedBody.data : undefined;
    const parsedFields = ProblemFieldsSchema.safeParse(body);
    const fields = parsedFields.success ? parsedFields.data : {};
    throw new ApiProblemError({
      kind: 'http',
      status: response.status,
      code: fields.code ?? String(response.status),
      title: blankToUndefined(fields.title),
      detail: blankToUndefined(fields.detail),
      body,
      userMessage: extractUserMessage(body),
      oauthUrl: blankToUndefined(fields.oauthUrl),
      retryAfterSec:
        typeof fields.retryAfterSec === 'number' && fields.retryAfterSec > 0 ? fields.retryAfterSec : undefined,
    });
  }

  // An empty or non-JSON 2xx body reads as `undefined` (a 204 against a
  // `z.undefined()` schema is valid); only a cancelled read is an abort.
  const parsedBody = await safeParseResponse(z.unknown(), response);
  if (!parsedBody.success && isAbortedRead(parsedBody.error)) {
    throw new ApiProblemError({
      kind: 'aborted',
      status: response.status,
      code: 'ABORTED',
      cause: parsedBody.error.cause,
    });
  }
  // Strip view: a tab on an older bundle must accept keys the server added since.
  const result = toStripView(schema).safeParse(parsedBody.success ? parsedBody.data : undefined);
  if (!result.success) {
    logApiValidationFailure(describeRequestInput(input), result.error.issues);
    throw new ApiProblemError({ kind: 'malformed', status: response.status, code: 'INVALID_RESPONSE' });
  }
  return result.data;
}

/**
 * The body of a route that answers with a plain `problem()`: `code`, `detail`
 * and `title` at the top level, plus an optional `issues` extension member.
 */
const ProblemEnvelopeSchema = z.object({
  code: z.string().optional().catch(undefined),
  detail: z.string().optional().catch(undefined),
  title: z.string().optional().catch(undefined),
  issues: z.array(z.unknown()).optional().catch(undefined),
});

/**
 * The `{ error: { code, message, issues } }` envelope older admin routes sent.
 * Read as a fallback for one release, so a tab on a newer bundle still reads a
 * server that has not deployed the plain `problem()` body yet.
 */
const LegacyEnvelopeSchema = z.object({
  // Each field is read on its own (`.catch`), so a wrongly-typed one does not hide the others.
  error: ErrorObjectSchema.extend({
    code: ErrorObjectSchema.shape.code.optional().catch(undefined),
    message: ErrorObjectSchema.shape.message.catch(undefined),
    issues: z.array(z.unknown()).optional().catch(undefined),
  }),
});

/** What `legacyEnvelopeError` returns. */
export type LegacyEnvelopeError = Error & { status: number; code?: string; issues?: unknown[] };

/**
 * Translate an `http` `ApiProblemError` from a blueprint-style admin route
 * into the `Error` those clients have always thrown. The fields come from the
 * top level of the body (`code`, `detail` else `title`, `issues`) and fall back
 * to the legacy `error.*` envelope. `message` is the first non-blank sentence,
 * else `API error <status>` (with `: invalid JSON response` when the body was
 * not JSON); `status`, `code` and `issues` ride along. A wrongly-typed field is
 * dropped on its own and does not hide the others.
 */
export function legacyEnvelopeError(e: ApiProblemError): LegacyEnvelopeError {
  const top = ProblemEnvelopeSchema.safeParse(e.body);
  const topFields = top.success ? top.data : {};
  const legacy = LegacyEnvelopeSchema.safeParse(e.body);
  const legacyFields = legacy.success ? legacy.data.error : {};
  const fallback = e.body === undefined ? `API error ${e.status}: invalid JSON response` : `API error ${e.status}`;
  return Object.assign(
    new Error(
      blankToUndefined(topFields.detail) ?? blankToUndefined(topFields.title) ?? legacyFields.message ?? fallback,
    ),
    {
      status: e.status,
      code: topFields.code ?? legacyFields.code,
      issues: topFields.issues ?? legacyFields.issues,
    },
  );
}

/** True for a 2xx whose body failed its schema — the write may have committed. */
export function isMalformedResponse(err: unknown): err is ApiProblemError {
  return err instanceof ApiProblemError && err.kind === 'malformed';
}

// `fetchRepSafeJson` lives here (not in `./api`) because this file is the
// sanctioned home for raw response-body parsing (terra/no-raw-response-json is
// off for it in .oxlintrc.json).
/**
 * Rep-safe wrapper around an `apiFetch` call + the body JSON parse — the shared fix
 * for finding 4-5 class errors (plan `2026-09-25-001-plays-tab-codex-fixes-
 * plan.md` U13): a rejected `fetch` (offline / network failure), the
 * `apiFetch` 15s/`timeoutMs` abort, a non-2xx response, and an unparsable
 * JSON body all used to reach `query.error.message` verbatim (`"Failed to
 * fetch"`, a raw `SyntaxError`, or — worse — a hardcoded status code like
 * `useFlowPlays`'s old `Failed to load plays (${res.status})`) and land in
 * front of a rep via `FlowTable error=`.
 *
 * Every failure path here resolves to `init.fallback` (or the server's own
 * `problem()`/error-envelope message for a non-ok response, via
 * `errorMessageFromResponse`) — with exactly one exception: if `apiFetch`
 * rejects AND `init.signal?.aborted` is already true, the ORIGINAL error is
 * rethrown rather than replaced. That case is React Query cancelling a
 * superseded request, not a failure a rep should ever see a message for —
 * rethrowing preserves React Query's own cancellation handling (it treats an
 * `AbortError` specially and does not surface it as `query.error`). An
 * `ApiProblemError` rejection (a `WriteRefusedError` from `apiFetch`) is
 * also rethrown as is, so it is never relabeled as a network failure.
 *
 * CONVENTION: `init.fallback` must be digit-free. A raw status code
 * (`Failed to load X (500)`) is exactly the leak this helper exists to
 * close — see `docs/no-raw-status-codes-to-reps.md` / team memory
 * "Never show a rep a raw status code".
 *
 * A REJECTED `apiFetch` (the branch below that isn't the cancellation
 * exception above) still carries the original error's TYPE and `.name`
 * onto the replacement — message swapped to `fallback`, but `instanceof
 * TypeError` / `.name === 'AbortError'` still hold. A pre-existing consumer
 * of `useFlowContacts` (`SignalDetail.tsx`'s `describeContactError`)
 * branches on exactly that shape to substitute the shared
 * `NETWORK_FALLBACK_TITLE` sentence — losing it here silently broke that
 * call site (a transport failure read as `fallback`, e.g. "Couldn't load
 * contacts right now.", instead of the shared network sentence).
 *
 * Callers as of U13: `useFlowPlays` (plays/hooks.ts) and `useFlowContacts`
 * (contact/hooks.ts). Candidates noted but out of scope for U13 (per the
 * plan): the other `contact/hooks.ts` queries (`useFlowContactTimeline`,
 * `useContactStateDetail`, `useFlowContactSignals`, `useFlowAccountIntel`)
 * already build their own fallback string inline via `errorMessageFromResponse`
 * and do not yet handle the reject/JSON-parse paths this helper adds.
 */
export async function fetchRepSafeJson(
  apiFetch: (input: RequestInfo | URL, init?: ApiFetchInit) => Promise<Response>,
  input: RequestInfo | URL,
  init: ApiFetchInit & { fallback: string },
): Promise<unknown> {
  const { fallback, ...fetchInit } = init;
  let response: Response;
  try {
    response = await apiFetch(input, fetchInit);
  } catch (err) {
    if (init.signal?.aborted || err instanceof ApiProblemError) throw err;
    const wrapped = err instanceof TypeError ? new TypeError(fallback) : new Error(fallback);
    if (err instanceof Error) wrapped.name = err.name;
    throw wrapped;
  }
  if (!response.ok) throw new Error(await errorMessageFromResponse(response, fallback));
  const parsed = await safeParseResponse(z.unknown(), response);
  if (!parsed.success) throw new Error(fallback);
  return parsed.data;
}

export function useApiFetchJson() {
  const { apiFetch } = useApi();
  return useCallback(
    <S extends z.ZodType>(schema: S, input: RequestInfo | URL, init?: ApiFetchInit): Promise<z.output<S>> =>
      fetchJson(apiFetch, schema, input, init),
    [apiFetch]
  );
}

/**
 * For inline renderers that want the two `problem()` fields (replaces
 * `components/admin/ranker/problem-body.ts`; U4b deletes that file and
 * switches its three callers to this).
 */
export function problemBodyOf(err: unknown): { title?: string; detail?: string } {
  if (!(err instanceof ApiProblemError)) return {};
  return { title: err.title, detail: err.detail };
}
